import { cn } from "@/lib/utils";

interface DoomsdayGaugeProps {
  current: number;
  target: number;
  size?: "sm" | "lg";
  className?: string;
}

export function DoomsdayGauge({ current, target, size = "sm", className }: DoomsdayGaugeProps) {
  const percentage = Math.min((current / target) * 100, 100);
  const dims = size === "lg" ? { width: 280, stroke: 16, fontSize: 32, subSize: 14 } : { width: 200, stroke: 12, fontSize: 24, subSize: 12 };
  const radius = (dims.width - dims.stroke) / 2;
  const circumference = 2 * Math.PI * radius;

  // Arc from 135° to 405° (270° sweep)
  const sweepAngle = 270;
  const arcLength = (sweepAngle / 360) * circumference;
  const filledLength = (percentage / 100) * arcLength;

  // Color based on percentage
  const getColor = () => {
    if (percentage < 25) return "#CC2936";
    if (percentage < 60) return "#EBF216";
    return "#44F2D2";
  };

  const color = getColor();

  return (
    <div className={cn("relative flex items-center justify-center", className)}>
      <svg width={dims.width} height={dims.width} viewBox={`0 0 ${dims.width} ${dims.width}`} className="transform -rotate-[135deg]">
        {/* Background arc */}
        <circle
          cx={dims.width / 2}
          cy={dims.width / 2}
          r={radius}
          fill="none"
          stroke="#1A2E2C"
          strokeWidth={dims.stroke}
          strokeDasharray={`${arcLength} ${circumference}`}
          strokeLinecap="round"
        />
        {/* Filled arc */}
        <circle
          cx={dims.width / 2}
          cy={dims.width / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={dims.stroke}
          strokeDasharray={`${filledLength} ${circumference}`}
          strokeLinecap="round"
          className="transition-all duration-1000 ease-out"
          style={{
            filter: `drop-shadow(0 0 8px ${color}40)`,
          }}
        />
      </svg>
      {/* Center text */}
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-mono font-bold text-foreground" style={{ fontSize: dims.fontSize }}>
          ${current.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
        </span>
        <span className="text-muted-foreground" style={{ fontSize: dims.subSize }}>
          / ${target.toLocaleString("en-US")}
        </span>
        <span className="font-mono text-xs mt-1" style={{ color }}>
          {percentage.toFixed(1)}%
        </span>
      </div>
    </div>
  );
}
